
import { defineComponent } from '@nuxtjs/composition-api'
import { stringify } from 'qs'
import transformQueryParam from '~/helper/transformQueryParam'
import { link } from '~/mixins/canonical'
import ErrorSerializer from '~/serializer/Error'
import type MetaBlock from '~/type/MetaBlock'
import type { FilterList } from '~/type/Filter'
import type Category from '~/type/Category'
import type Constructor from '~/api/constructor.api'
type RequestType = Awaited<ReturnType<typeof Constructor.catalog>>['result']

interface ParamsType {
  cancel?: boolean
  seo?: string
  customQuery?: { [x: string]: string | (string | null)[] }
  page?: number
  sort?: string | (string | null)[]
  availabilitySort?: boolean
}

export default defineComponent({
  inject: ['emitter'],
  async asyncData(context) {
    try {
      const params = transformQueryParam({
        query: context.query,
        path: context.route.path,
      })

      const response = (await context.$api.constructor.catalog.call(context, { ...params }))

      return {
        initialValue: response.result,
        params: {
          ...params,
          cancel: true,
        },
      }
    }
    catch (e) {
      const error = ErrorSerializer(e)
      new context.$pageError({
        ...error,
        message: error.code === 404 ? 'Категория каталога не найдена' : error.message,
      })
    }
  },
  data(): { initialValue?: RequestType; params: ParamsType } {
    return {
      initialValue: undefined,
      params: {
        cancel: true,
      },
    }
  },
  head() {
    return link.call(this)
  },
  computed: {
    isBonusesPage() {
      return this.$route.path === '/catalog/bonuses/'
    },
    linkPage() {
      const query = { ...this.params.customQuery }
      if (query.PAGEN_1)
        delete query.PAGEN_1
      const queryLength = Object.keys(query)?.length || 0
      return queryLength ? `${this.$route.path}?${stringify(query)}&` : `${this.$route.path}?`
    },

  },
  mounted() {
    if (this.initialValue) {
      this.$emitter.emit('product-list-view', {
        list: this.initialValue?.product,
        listName: 'Каталог',
      })

      this.$emitter.emit('view-category', {
        id: this.initialValue?.id,
        name: this.initialValue?.title,
      })
    }
  },
  methods: {
    semanticData({ title, sectionNav, meta, count, filter }: { title: string; count: number; meta: MetaBlock; filter: { main: FilterList }; sectionNav: Category[] }) {
      return {
        '@context': 'http://schema.org',
        '@type': 'Product',
        'name': title,
        'image': sectionNav?.[0]?.children?.[0]?.image,
        'description': meta?.category?.description,
        'url': process.env.projectURL + this.$route.path,
        'brand': {
          '@type': 'Brand',
          'name': 'Интернет-магазин «05.ru»',
          'logo': require('~/assets/images/logo-80px.svg'),
        },
        'offers': {
          '@type': 'AggregateOffer',
          'offerCount': count,
          'lowPrice': filter.main.list[0].type === 'range' ? filter.main.list[0].filter.min : 0,
          'highPrice': filter.main.list[0].type === 'range' ? filter.main.list[0].filter.max : 0,
          'priceCurrency': 'RUB',
          'seller': {
            '@type': 'Organization',
            'name': 'Интернет-магазин «05.ru»',
          },
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '5.0',
          'bestRating': '5.0',
          'worstRating': '1.0',
          'ratingCount': '79',
        },
      }
    },
    changeParams(data: ParamsType) {
      const params = { ...this.params, ...data }
      /**
       * Нужно чтобы после активации фильтров, params.seo на бонусной странице всегда оставалось в значении '/catalog/bonuses/'.
       * Это нужно чтобы бонусная страница работала корректно, потому-что в модуле апи, в запросе передаются некоторые уникальные параметры и значения
       * которые активируются строго по условию когда seo === '/catalog/bonuses/'. Так было сделано, потому-что бэк не принимает seo вида: '/catalog/bonuses/filter/'
      */
      if (params?.seo && params?.customQuery)
        params.seo = this.isBonusesPage ? '/catalog/bonuses/' : this.getPageURL(params?.seo, Object.keys(params?.customQuery)?.length)
      this.params = params
    },
    getPageURL(current: string, activeFilterCount: number) {
      let path = current
      if (!path.includes('filter/')) {
        if (activeFilterCount > 0)
          path = `${path}filter/`
      }
      else if (activeFilterCount === 0 && current.includes('filter/')) {
        path = path.replace('filter/', '')
      }
      return path
    },
    changeQuery(data: ParamsType) {
      const query = { ...this.params, ...data }

      history.pushState(
        null,
        '',
        `${query.seo}?${
         stringify({
          ...query.customQuery,
          PAGEN_1: (query.page && query.page > 1) ? query.page : null,
          availability_sort: query.availabilitySort,
          sort: query.sort,
        })}`,
      )
    },
  },
})
